import { useFirePostOfficeAnalyticsEvents } from '@post-office/analytics';

import { usePostOfficeUserInteractions } from '@atlassian/post-office-analytics/interactions';

import { useMessageEvent } from '@post-office/message-lifecycle';

import type { Collaborator, TargetProduct } from './types';

import { useCallback, useMemo, type MouseEvent } from 'react';

import {
	EnablementTouchpointEnum,
	type FetchInput,
	type FetchResponse,
	getTraceId,
	type Product,
	requestAccess as requestAccessService,
} from '@atlassian/expansion-services/services';

import {
	useServiceCore as useService,
	type DependencyOverridesOf,
} from '@atlassian/expansion-services/core';

import type { GasCorePayload } from '@atlaskit/analytics-gas-types';

type Params = {
	tenantId: string;
	userId: string;
	targetProduct: TargetProduct;
	collaborators: Collaborator[];
	sourceProduct: Product;
	crossJoinUrl: string;
	showRequestSentFlag: () => void;
	messageTemplateId?: string;
	onComplete: () => void;
};

export const CJN_SNOOZE_DAYS = 1;

const targetProductByVariant: Record<TargetProduct, Product> = {
	loom: 'loom',
	'jira-product-discovery': 'jira-product-discovery',
	confluence: 'confluence',
};

type RequestAccessDependencies = Omit<
	DependencyOverridesOf<typeof requestAccessService>,
	'createAnalyticsEvent'
> & { createAnalyticsEvent: (payload: GasCorePayload) => { fire: () => void } };

const isSupportedEventType = (
	eventType: GasCorePayload['eventType'],
): eventType is 'ui' | 'track' | 'operational' => eventType !== 'screen';

const createAnalyticsEventHandler = (
	fireAnalyticsEvent: ReturnType<typeof useFirePostOfficeAnalyticsEvents>['fireAnalyticsEvent'],
	messageTemplateId: string | undefined,
) => {
	return (payload: GasCorePayload) => ({
		fire: () => {
			if (!payload.action || !isSupportedEventType(payload.eventType)) {
				// Unable to translate event type
				return;
			}

			fireAnalyticsEvent({
				eventType: payload.eventType,
				action: payload.action,
				actionSubject: payload.actionSubject,
				actionSubjectId: payload.actionSubjectId,
				attributes: { ...payload.attributes, messageTemplateId },
			});
		},
	});
};

const fetchJSON = async <Response>(input: FetchInput): Promise<FetchResponse<Response>> => {
	const { url, ...options } = input;
	const response = await fetch(url, options);
	if (!response.ok) {
		throw new Error(`Fetch failed with status ${response.status}`);
	}
	const data = await response.json();
	const traceId = getTraceId(response);
	return traceId === undefined ? { data } : { data, traceId };
};

export const useRequestAccess = ({
	targetProduct,
	tenantId,
	userId,
	sourceProduct,
	collaborators,
	crossJoinUrl,
	showRequestSentFlag,
	messageTemplateId,
	onComplete,
}: Params): ((event: MouseEvent<HTMLAnchorElement>) => Promise<void>) => {
	const { onCtaActioned } = usePostOfficeUserInteractions();
	const { messageSnoozed } = useMessageEvent();
	const { fireAnalyticsEvent } = useFirePostOfficeAnalyticsEvents();
	const deps = useMemo(
		(): RequestAccessDependencies => ({
			createAnalyticsEvent: createAnalyticsEventHandler(fireAnalyticsEvent, messageTemplateId),
			fetchJSON,
			getStargateURL: (path: string) => `/gateway/api${path}`,
			getCurrentCloudId: () => tenantId,
			getCurrentProduct: () => sourceProduct,
			getCurrentUserId: () => userId,
		}),
		[fireAnalyticsEvent, tenantId, sourceProduct, userId],
	);
	const requestAccess = useService(
		requestAccessService,
		deps as DependencyOverridesOf<typeof requestAccessService>,
	);

	return useCallback(
		async (clickEvent: MouseEvent<HTMLAnchorElement>): Promise<void> => {
			clickEvent.preventDefault();
			onCtaActioned({
				interactionIdentifier: 'cjnNav4',
				destinationUrl: crossJoinUrl,
				additionalMetadata: {
					targetProduct,
					collaboratorCount: collaborators.length,
					...(messageTemplateId ? { messageTemplateId } : {}),
				},
			});
			void fireAnalyticsEvent({
				eventType: 'ui',
				action: 'clicked',
				actionSubject: 'link',
				actionSubjectId: 'cjnNav4',
				attributes: { messageTemplateId },
			});
			try {
				const result = await requestAccess({
					product: targetProductByVariant[targetProduct],
					integration: 'jira-side-nav-po-placement',
					touchpoint: EnablementTouchpointEnum.CROSSJOIN_NUDGE,
				});

				if (result.error) {
					fireAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'requestAccess',
						actionSubjectId: 'cjnNav4',
						attributes: {
							team: 'puma',
							targetProduct,
							messageTemplateId,
							errorMessage: result.error.message,
						},
					});
					if (typeof globalThis !== 'undefined' && globalThis.window) {
						globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
					}
				} else if (result.result === 'PENDING_REQUEST_CREATED') {
					showRequestSentFlag();
				} else if (typeof globalThis !== 'undefined' && globalThis.window) {
					globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
				}
			} catch (error) {
				fireAnalyticsEvent({
					eventType: 'operational',
					action: 'unexpectedFailure',
					actionSubject: 'requestAccess',
					actionSubjectId: 'cjnNav4',
					attributes: {
						team: 'puma',
						targetProduct,
						messageTemplateId,
						errorMessage: error instanceof Error ? error.message : undefined,
					},
				});
				if (typeof globalThis !== 'undefined' && globalThis.window) {
					globalThis.window.open(crossJoinUrl, '_blank', 'noreferrer');
				}
			} finally {
				// Snooze on every outcome: the user has acted, so the nudge should not re-appear
				// while the capabilities cache can still report the pre-action state. Best-effort,
				// so a failure here must not affect the access flow the user just completed.
				void messageSnoozed(CJN_SNOOZE_DAYS).catch((error: unknown) => {
					fireAnalyticsEvent({
						eventType: 'operational',
						action: 'failed',
						actionSubject: 'messageSnoozed',
						actionSubjectId: 'cjnNav4',
						attributes: {
							team: 'puma',
							targetProduct,
							messageTemplateId,
							errorMessage: error instanceof Error ? error.message : undefined,
						},
					});
				});
				onComplete();
			}
		},
		[
			onCtaActioned,
			messageSnoozed,
			crossJoinUrl,
			targetProduct,
			collaborators,
			requestAccess,
			showRequestSentFlag,
			fireAnalyticsEvent,
		],
	);
};
